import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs/Subject';

import { Store } from '@ngrx/store';
import { AppState } from '../../app.state';

import { authenticatedPatientSelector } from '../../../main/selectors/patients-selectors';
import { ShowFlash } from '../../shared/flash/store/flash.actions';

import { ContactMethod } from '../../shared/models/contact-method.model';
import { Group } from '../../shared/models/group.model';

import { cloneDeep } from 'lodash';

@Component({
  selector: 'reminders',
  template: require('./reminders.component.html'),
  styles: [require('./reminders.component.scss')],
})
export class RemindersComponent implements OnInit, OnDestroy {
  remindersForm: FormGroup;
  availableKinds: any;
  submitted: boolean;
  notificationPreference: any = {};
  selectedNumber: number;
  loading: boolean = false;
  contactMethods: any[] = [];

  private initialContactMethod: any;
  private initialContactMethods: any;
  private subscription: any;
  private group: Group;
  private patientId: number;
  private email: string;
  private destroyed: Subject<boolean> = new Subject();

  constructor(
    @Inject('$translate') private $translate,
    @Inject('ProductService') private productService,
    @Inject('PatientsService') private patientsService,
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    this.setAvailableKinds()

    this.store.select((state: AppState): Group => state.group)
      .takeUntil(this.destroyed)
      .subscribe((group: Group): void => { this.group = group; });

    this.remindersForm = this.formBuilder.group({
      kinds: ['', Validators.required],
    });

    this.store.select(authenticatedPatientSelector)
      .takeUntil(this.destroyed)
      .subscribe((patient): void => {
        if(patient !== null) {
          const {
            id,
            subscription,
            profile: { email, contactMethods },
          } = patient;

          this.patientId = id;
          this.subscription = cloneDeep(subscription);
          this.notificationPreference = cloneDeep(subscription.notificationPreference);
          this.contactMethods = cloneDeep(contactMethods);
          this.initialContactMethods = cloneDeep(contactMethods);
          this.initialContactMethod = cloneDeep(this.contactMethods[0]);

          this.email = email;

          this.remindersForm.setValue({ kinds: this.notificationPreference });
        }
      });

    this.remindersForm.get('kinds').valueChanges
      .takeUntil(this.destroyed)
      .subscribe(() => {
        this.contactMethods = this.contactMethods.filter((contactMethod): boolean => !!contactMethod.id);
        this.selectedNumber = null;
      })
  }

  ngOnDestroy(): void {
    this.destroyed.next(true);
  }

  submit(): void {
    if (!this.remindersForm.valid) {
      return;
    }

    this.loading = true;

    if (this.remindersKind !== 'email') {
      const contactMethods = [
        this.selectedContactMethod,
        ...this.contactMethods.filter((contactMethod) => contactMethod.displaySequence !== this.selectedNumber),
      ].map((contactMethod) => {
        if (contactMethod !== null && contactMethod.kind !== null && contactMethod.number !== null && contactMethod.number.length > 0) {
          return {
            kind: {
              mnemonic: contactMethod.kind,
            },
            number: contactMethod.number,
          };
        }
      }).filter(contactMethod => !!contactMethod);

      this.patientsService
        .updateContactMethods(this.patientId, this.group.id, contactMethods)
        .then(() => {
          this.saveNotificatioPreferences();
        });
    } else {
      this.saveNotificatioPreferences();
    }
  }

  saveNotificatioPreferences(): void {
    const message: string = this.contactMethods.length > this.initialContactMethods.length ? 'update.reminders.update_success_added_phone' : 'update.reminders.update_success';

    this.subscription.notificationPreference = this.remindersKind;

    this.subscription.$saveNotificationPreference(() => {
      this.patientsService
        .loadPatient(this.patientId)
        .then(() => {
          this.loading = false;
          this.store.dispatch(new ShowFlash({ name: 'update', expiresAfter: 2500, additionalInfos: { title: message } }));
        });
    });
  }

  cancel(): void {
    this.contactMethods = cloneDeep(this.initialContactMethods);
    this.remindersForm.setValue({ kinds: this.notificationPreference });
    this.selectedNumber = null;
  }

  addBlankContactMethod(type: 'home' | 'mobile'): void {
    this.contactMethods.push({
      displaySequence: this.contactMethods.length + 1,
      id: null,
      kind: type,
      number: '',
    });
  }

  onSelectedNumberChange(selectedNumber: number): void {
    this.selectedNumber = selectedNumber;
  }

  private setAvailableKinds(): void {
    this.availableKinds = [];

    this.availableKinds.push({
      value: 'email',
      text: this.$translate.instant('update.reminders.contact_method_kinds.email'),
    });

    if (this.productService.isAvailable('PATIENT_SMS_REMINDERS')) {
      this.availableKinds.push({
        value: 'sms',
        text: this.$translate.instant('update.reminders.contact_method_kinds.sms'),
      });
    }

    if (this.productService.isAvailable('PATIENT_PHONE_REMINDERS')) {
      this.availableKinds.push({
        value: 'phone',
        text: this.$translate.instant('update.reminders.contact_method_kinds.phone'),
      });
    }
  }

  get canSubmit(): boolean {
    return !this.loading
      && !this.isInitialNotificationPreference
      && !this.contactMethods.filter((contactMethod) => contactMethod.displaySequence === this.selectedNumber && !contactMethod.number).length
      && !(this.remindersKind === 'sms' && !this.mobileContactMethods.length)
      && !(this.remindersKind === 'phone' && !this.contactMethods.length)
      && !(!this.selectedNumber && this.remindersKind !== 'email');
  }

  get isInitialNotificationPreference(): boolean {
    return this.contactMethods.length
      && this.notificationPreference === this.remindersKind
      && this.selectedNumber === this.contactMethods[0].displaySequence
      && this.initialContactMethod.number === this.contactMethods[0].number
      && this.initialContactMethod.kind === this.contactMethods[0].kind;
  }

  get currentNotificationMethod(): string {
    switch (this.remindersKind) {
      case 'email':
        return this.email;

      case 'sms': {
        const mobileNumbers = this.contactMethods
          .filter(method => method.kind === 'mobile')
          .sort((a, b) => a.displaySequence - b.displaySequence);

        if (mobileNumbers.length) {
          const selected = mobileNumbers.find((mobileNumber) => mobileNumber.displaySequence === this.selectedNumber);

          if (selected) {
            return selected.number;
          }

          return mobileNumbers[0].number;
        }
        break;
      }

      case 'phone': {
        const phoneNumbers = this.contactMethods
          .sort((a, b) => a.displaySequence - b.displaySequence);

        if (phoneNumbers.length) {
          const selected = phoneNumbers.find((phoneNumber) => phoneNumber.displaySequence === this.selectedNumber);

          if (selected) {
            return selected.number;
          }

          return phoneNumbers[0].number;
        }
        break;
      }

      default:
    }

    return null;
  }

  get remindersKind(): string {
    return this.remindersForm.controls.kinds.value;
  }

  get mobileContactMethods(): ContactMethod[] {
    return this.contactMethods.filter((contactMethod): boolean => contactMethod.kind === 'mobile');
  }

  private get selectedContactMethod(): any {
    return this.contactMethods.find((contactMethod): boolean => contactMethod.displaySequence === this.selectedNumber);
  }
}



// WEBPACK FOOTER //
// ../node_modules/angular2-template-loader!./ng2/profile/reminders/reminders.component.ts